<template>
  <div class="card overflow-hidden">
    <div class="card-body pt-3 pb-4">
      <div class="row">
        <div class="col-12 d-flex justify-content-between">
          <div v-if="period" class="pt-2 text-lighter">
            <i class="fa fa-clock"></i
            ><span class="ms-2 font-weight-bold">{{
              formatPeriod(period)
            }}</span>
          </div>
          <div class="d-flex">
            <div class="form-group mx-3">
              <SectionDropdown
                id="ddSectionFilterRecoveryData"
                :selected="selectedSection"
                @selected-section-id="
                  (selectedValue) => (selectedSection = selectedValue)
                "
              />
            </div>
          </div>
        </div>
        <div class="col-lg-8 col-12">
          <div class="text-xs text-uppercase text-primary text-bold mb-1">
            {{ header }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{
            getSectionName(selectedSection)
          }}</span>

          <div v-if="!hasData" class="text-muted py-3">
            No data found for the selected date range.
          </div>
          <div v-show="hasData" class="my-4 chart">
            <canvas
              :id="`${id}-recovery`"
              class="chart-canvas"
              :height="height"
            ></canvas>
          </div>
          <hr class="hr horizontal dark" />
          <div class="text-xs text-uppercase text-primary text-bold mb-1 mt-4">
            {{ headerRhr }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{
            getSectionName(selectedSection)
          }}</span>

          <div v-if="!hasData" class="text-muted py-3">
            No data found for the selected date range.
          </div>
          <div v-show="hasRhrData" class="my-4 chart">
            <canvas
              :id="`${id}-rhr`"
              class="chart-canvas"
              :height="height"
            ></canvas>
          </div>
        </div>

        <div v-show="hasData" class="col-lg-4 col-12">
          <div
            class="d-flex flex-column mx-3 mb-3 position-relative mt-sm-4 ms-sm-0 help"
          >
            <div class="text-bolder mb-2">What is Recovery?</div>
            <div class="mb-3 content">
              Recovery is a measure of how well your body adapts to various
              stressors - physical, mental and emotional. It tells you how
              prepared your body is to perform.
            </div>
            <div class="content">
              After a night’s sleep, your recovery is calculated as a
              percentage, and shown as red, orange, or green.
            </div>
            <div class="text-bolder mt-3 mb-2">How is Recovery measured?</div>
            <div class="mb-3 content">
              Recovery is based on heart rate variability (HRV), resting heart
              rate (RHR) and sleep.
            </div>
            <div class="content">
              Scored on a scale of 0-100%, it gives you a better understanding
              of when to take up more strain and when to let your body rest.
            </div>

            <!-- table-->
            <div class="p-3 mt-3 mb-2 bg-help-table">
              <div class="d-flex content">
                <div class="w-30">
                  <span class="square me-2 bg-danger"></span>0 - 33%
                </div>
                <div class="text-break">
                  Prioritise rest and relaxing activities
                </div>
              </div>

              <div class="d-flex content mt-2">
                <div class="w-30">
                  <span class="square me-2 bg-warning"></span>34 - 66%
                </div>
                <div class="text-break">Ready to take on moderate strain</div>
              </div>

              <div class="d-flex content mt-2">
                <div class="w-30">
                  <span class="square me-2 bg-success"></span>67 - 100%
                </div>
                <div class="text-break">
                  Ready to handle vigorous activities
                </div>
              </div>
            </div>

            <div class="text-bolder mt-3 mb-2">
              What is HRV (Heart Rate Variability)?
            </div>
            <div class="mb-3 content">
              HRV is a measure of the variation in time between each heartbeat.
              A higher HRV indicates a more adaptable and resilient autonomic
              nervous system, associated with better cardiovascular health and
              overall well-being.
            </div>
            <div class="text-bolder mt-3 mb-2">
              What is RHR (Resting Heart Rate)?
            </div>
            <div class="mb-3 content">
              RHR is the number of heartbeats per minute when the body is at
              complete rest. A lower resting heart rate is generally considered
              a positive indicator of cardiovascular fitness.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const dayjs = require("dayjs");
let duration = require("dayjs/plugin/duration");
dayjs.extend(duration);

import Constants from "@/helpers/constants";
import Utils from "@/helpers/utils";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import styles from "./RecoverySectionChart.vue?vue&type=style&index=0&lang=scss&module=1";
import SectionDropdown from "@/views/pages/components/SectionDropdown.vue";

Chart.register(annotationPlugin);

export default {
  name: "RecoverySectionChart",
  components: {
    SectionDropdown,
  },
  props: {
    id: {
      type: String,
      default: "recovery-section-chart",
    },
    period: {
      type: Array,
      required: true,
    },
    header: {
      type: String,
      default: "Recovery Chart",
    },
    headerRhr: {
      type: String,
      default: "RHR Chart",
    },
    title: {
      type: String,
      default: "Today",
    },
    height: {
      type: [String, Number],
      default: "200",
    },
    selectedSectionId: {
      type: Number,
      default: -1,
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
        color: String,
      },
    },
    chartRhr: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
        color: String,
      },
    },
  },
  emits: ["selected-section-id"],
  data() {
    return {
      selectedSection: Constants.DEFAULT_SECTION,
      alerts: [],
      annotationsAvg: [],
    };
  },
  computed: {
    hasData() {
      return this.chart?.datasets?.length > 0;
    },
    hasRhrData() {
      return this.chartRhr?.datasets?.length > 0;
    },
  },
  watch: {
    chart() {
      if (this.hasData) {
        this.addChart();
      }
    },
    chartRhr() {
      if (this.hasRhrData) {
        this.addRhrChart();
      }
    },
    selectedSection() {
      this.$emit("selected-section-id", this.selectedSection);
    },
    selectedSectionId() {
      this.selectedSection = this.selectedSectionId;
    },
  },
  methods: {
    formatPeriod(period) {
      return Utils.formatPeriod(period);
    },
    getSectionName(section_id) {
      const section = this.$store.getters["list/getSectionName"](section_id);
      return section ? section.name : "";
    },
    addChart() {
      const id = this.id + "-recovery";
      const ctx = document.getElementById(id).getContext("2d");

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
      gradientStroke1.addColorStop(1, "rgba(59,121,130,0.15)");
      gradientStroke1.addColorStop(0.5, "transparent");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      new Chart(ctx, {
        data: {
          labels: this.chart.labels,
          datasets: [
            {
              type: "bar",
              label: this.chart.datasets[0].label,
              pointBackgroundColor: styles.colorRecovery,
              borderColor: styles.colorRecovery,
              backgroundColor: styles.colorRecovery,
              data: this.chart.datasets[0].data,
              order: 2,
              yAxisID: "y1",
              tension: 0.4,
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
              maxBarThickness: 10,
            },
            {
              type: "line",
              label: this.chart.datasets[1].label,
              data: this.chart.datasets[1].data,
              tension: 0.4,
              pointRadius: 3,
              pointBackgroundColor: styles.colorRecoveryLight,
              borderColor: styles.colorRecoveryLight,
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke1,
              fill: true,
              order: 1,
              yAxisID: "y",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            annotation: {
              annotations: {
                line1: {
                  type: "line",
                  borderColor: styles.colorLimitLineWarning,
                  borderDash: [3, 3],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    position: "start",
                  },
                  scaleID: "y1",
                  value: 66,
                },
                line2: {
                  type: "line",
                  borderColor: styles.colorLimitlineDanger,
                  borderDash: [3, 3],
                  borderDashOffset: 0,
                  borderWidth: 1,
                  label: {
                    display: true,
                    position: "start",
                  },
                  scaleID: "y1",
                  value: 33,
                },
              },
            },
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y1: {
              display: true,
              position: "left",
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[0].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true, // only want the grid lines for one axis to show up
              },
            },
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "right",
              beginAtZero: true,
              ticks: {
                stepSize: 10,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[1].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: false,
              },
            },
          },
        },
      });
    },
    addRhrChart() {
      const id = this.id + "-rhr";
      const ctx = document.getElementById(id).getContext("2d");

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
      gradientStroke1.addColorStop(1, "rgba(59,121,130,0.15)");
      gradientStroke1.addColorStop(0.5, "transparent");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      new Chart(ctx, {
        data: {
          labels: this.chartRhr.labels,
          datasets: [
            {
              type: "bar",
              label: this.chartRhr.datasets[0].label,
              pointBackgroundColor: styles.colorRecoveryRhr,
              borderColor: styles.colorRecoveryRhr,
              backgroundColor: styles.colorRecoveryRhr,
              data: this.chartRhr.datasets[0].data,
              order: 2,
              yAxisID: "y",
              tension: 0.4,
              borderWidth: 1,
              borderRadius: 4,
              borderSkipped: false,
              maxBarThickness: 10,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            x: {
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "left",
              beginAtZero: false,
              ticks: {
                stepSize: 5,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chartRhr.datasets[0].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: false,
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
:export {
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  colorRecoveryLight: $g-secondary_300;
  colorRecoveryRhr: $g-secondary_500;
  colorRecovery: $color-recovery;
  colorAxes: $color-chart-axes-text;
  colorLimitlineDanger: $color-danger;
  colorLimitLineWarning: $color-warning;
}
</style>
